// @ts-nocheck
import PaymentDetailsSkeleton from '../../../components/Skeleton/PaymentDetailsSkeleton/PaymentDetailsSkeleton';
import { Box, Typography, Divider } from '@mui/material';
import { Check } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { useLocation, useLoaderData } from 'react-router-dom';
import { getBookingById } from '../../../services/booking/booking';
import { getOutputTrip } from '../../../services/output/trip';
import { useEffect, useState } from 'react';
import {
  formatCurrency,
  roundCurrency,
  getConfig
} from '../../../utils/currencyHelper';
import { calculatePaymentScheduleNew } from '../../../utils/paymentScheduleNew';
import { formatDate } from '../../../utils/dateUtility';
import {
  formatTravelDate,
  normalizeTravelDate
} from '../../../utils/travelDate';
import { formatStoredPaymentAmount } from '../../../utils/paymentDisplay';
import {
  getSavedPaymentAmounts,
  getPaymentTransactionId
} from '../../../utils/paymentSuccess';
import * as PropTypes from 'prop-types';
import './SuccessfulPaymentMessage.css';

/**
 * Functional React component for rendering a custom successful payment message.
 *
 * @namespace Components
 *
 * @param {Object} props - The component's properties
 * @param {string} [props.id] - The ID of the component.
 * @param {...any} props.rest - Additional props to be spread on the parent element.
 *
 * @returns {JSX.Element} React element representing the successful payment message.
 */

const SuccessfulPaymentMessage = ({ id, ...rest }) => {
  const { t, i18n } = useTranslation(['trip', 'common', 'bookings']);
  const { bookingId } = useLoaderData();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const bookinKey = queryParams.get('bookingkey');
  const tripId = queryParams.get('tripid');
  const [bookingData, setBookingData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [nextInstallment, setNextInstallment] = useState(null);
  const state = location.state || {};
  const { amount, payerEmail = '', paymentMethod = 'card', tripname } = state;
  const transactionId = getPaymentTransactionId(
    queryParams.get('transactionid') ?? state.transactionId
  );
  const savedPayment = getSavedPaymentAmounts(
    bookingData?.paymentTransaction || state.transaction,
    transactionId,
    bookingId
  );
  const tenantCurrency = bookingData?.tripData?.tenant?.sellingcurrency;
  const operatorConfig = tenantCurrency
    ? getConfig({
        currencyCode: tenantCurrency.currencycode,
        currencySymbol: tenantCurrency.currencysymbol
      })
    : getConfig();
  const locale = i18n?.resolvedLanguage || i18n?.language || 'en';
  const isACH =
    (bookingData?.paymentTransaction?.paymentmethod || paymentMethod) ===
    'us_bank_account';
  const tripTitle = tripname || bookingData?.trip?.tripname || '';
  const parsedPaymentAmount = amount == null ? NaN : Number(amount);
  const paymentAmount = Number.isFinite(parsedPaymentAmount)
    ? parsedPaymentAmount
    : null;
  const displayedPayment = savedPayment
    ? formatStoredPaymentAmount(
        savedPayment.amount,
        savedPayment.currency,
        locale
      )
    : queryParams.has('transactionid') ||
        state.transactionId != null ||
        state.transaction?.chargecurrency ||
        paymentAmount == null
      ? t('bookings:paymentDetailsUnavailable')
      : formatCurrency(paymentAmount, operatorConfig);
  const parsedBalanceDue =
    bookingData?.balancedue == null ? NaN : Number(bookingData.balancedue);
  const balanceDue = Number.isFinite(parsedBalanceDue)
    ? parsedBalanceDue
    : null;
  const contactEmail = payerEmail || '';
  const dateFormat = t('common:dateFormat');

  const nextPaymentDate =
    nextInstallment?.date && formatTravelDate(nextInstallment.date, dateFormat);

  useEffect(() => {
    let canceled = false;
    setBookingData(null);
    setIsLoading(true);
    const getData = async () => {
      try {
        if (!bookingId) return;
        const [promiseBooking, promiseTrip] = await Promise.all([
          getBookingById(bookingId, bookinKey, transactionId ?? undefined),
          getOutputTrip(tripId)
        ]);
        if (!canceled && promiseBooking?.data) {
          setBookingData({
            ...promiseBooking.data,
            tripData: promiseTrip?.data
          });
        }
      } catch {
        // A missing payment snapshot must not become another payment's total.
      } finally {
        if (!canceled) setIsLoading(false);
      }
    };
    getData();
    return () => {
      canceled = true;
    };
  }, [bookingId, bookinKey, tripId, transactionId]);

  useEffect(() => {
    const paymentPlan = bookingData?.tripData?.paymentplan;
    const bookingPlans = bookingData?.bookingplans;

    if (!bookingPlans?.length || !paymentPlan) {
      setNextInstallment(null);
      return;
    }

    const tripStartDate = normalizeTravelDate(bookingData?.tripData?.startdate);
    const bookingCreationDate =
      formatDate(bookingData?.bookingdate, 'YYYY-MM-DD') ||
      formatDate(bookingData?.createddatetime, 'YYYY-MM-DD') ||
      formatDate(new Date().toISOString(), 'YYYY-MM-DD');

    if (!tripStartDate || !bookingCreationDate) {
      setNextInstallment(null);

      return;
    }

    const mappedPlans = bookingPlans
      .map((plan) => {
        const occupancy = Number(
          plan?.occupancy ?? plan?.tripplan?.occupancy ?? 0
        );
        const pricePerPerson = Number(
          plan?.priceperperson ?? plan?.tripplan?.priceperperson ?? 0
        );
        const deadline = plan?.tripplan?.lastbookdate
          ? normalizeTravelDate(plan.tripplan.lastbookdate)
          : null;

        return {
          occupancy,
          pricePerPerson,
          bookingDeadline: deadline
        };
      })
      .filter((plan) => plan.occupancy > 0 && isFinite(plan.pricePerPerson));

    if (!mappedPlans.length) {
      setNextInstallment(null);
      return;
    }

    const existingAddOns = bookingData.bookingpassengeraddons || [];
    const addOnsMap = new Map();

    // Group existing add-ons by tripaddonid
    existingAddOns.forEach((addon) => {
      const {
        tripaddonid,
        bookingpassengerid,
        bookingpassengeraddonid,
        priceperperson,
        dueatbooking,
        title,
        description
      } = addon;

      if (!addOnsMap.has(tripaddonid)) {
        addOnsMap.set(tripaddonid, {
          tripaddonid,
          title: title || '',
          description: description || '',
          pricePerPerson: Number(priceperperson) || 0,
          dueAtBooking: dueatbooking ?? false,
          passengers: []
        });
      }

      addOnsMap.get(tripaddonid).passengers.push({
        bookingpassengerid,
        bookingpassengeraddonid // Not null = existing add-on
      });
    });

    const addOns = Array.from(addOnsMap.values());

    // Map add-ons from unified booking state for payment schedule calculation
    // bookingState.addOns contains both existing and newly selected add-ons
    const bookingAddOns = addOns.map((addOn) => {
      const passengerCount = addOn.passengers.length;
      const totalPrice = addOn.pricePerPerson * passengerCount;

      return {
        pricePerPerson: totalPrice, // Total price for all passengers of this add-on
        dueAtBooking: addOn.dueAtBooking
      };
    });

    const schedule = calculatePaymentScheduleNew({
      tripStartDate,
      bookingCreationDate,
      bookingPlans: mappedPlans,
      paymentPlan: {
        depositAmount: paymentPlan?.depositamount || '100%',
        secondPaymentAmount: paymentPlan?.secondpaymentamount ?? null,
        secondPaymentDue: paymentPlan?.secondpaymentdue ?? null,
        thirdPaymentAmount: paymentPlan?.thirdpaymentamount ?? null,
        thirdPaymentDue: paymentPlan?.thirdpaymentdue ?? null,
        fourthPaymentAmount: paymentPlan?.fourthpaymentamount ?? null,
        fourthPaymentDue: paymentPlan?.fourthpaymentdue ?? null,
        finalPaymentDue: paymentPlan?.finalpaymentdue ?? 0,
        enablePartialPayments: paymentPlan?.allowpartialpayment ?? true,
        allowBookingWithoutPayment:
          paymentPlan?.allowbookingwithoutpayment ?? false
      },
      priceAdjustment: bookingData?.priceadjustment ?? 0,
      bookingAddOns
    });

    const entries = schedule?.allEntries ?? [];

    if (!entries.length) {
      setNextInstallment(null);
      return;
    }

    // Create payment schedule FOR PaymentForm with cumulative tracking
    const totalReceived = roundCurrency(bookingData?.totalreceived ?? 0);
    const today = formatDate(new Date().toISOString(), 'YYYY-MM-DD');

    // Build cumulative expected amounts structure
    let cumulativeExpected = 0;
    const enrichedEntries = entries.map((entry, index) => {
      cumulativeExpected = roundCurrency(cumulativeExpected + entry.amount);

      const isFirstPayment = entry.label === 'booking' || index === 0;
      const isFinalPayment =
        entry.label === 'final' || index === entries.length - 1;

      let type = 'installment';
      if (isFirstPayment) {
        type = 'booking';
      } else if (isFinalPayment) {
        type = 'final';
      }

      // Calculate what should be outstanding for this specific installment
      // by comparing cumulative expected vs total received
      const cumulativeExpectedUpToThisPoint = cumulativeExpected;
      const cumulativeExpectedBeforeThisPoint = roundCurrency(
        cumulativeExpected - entry.amount
      );

      // Amount outstanding for THIS installment specifically
      let outstandingForThisInstallment = 0;
      if (totalReceived < cumulativeExpectedUpToThisPoint) {
        // Some or all of this installment is unpaid
        if (totalReceived <= cumulativeExpectedBeforeThisPoint) {
          // Haven't reached this installment yet - full amount outstanding
          outstandingForThisInstallment = entry.amount;
        } else {
          // Partially paid this installment
          outstandingForThisInstallment = roundCurrency(
            cumulativeExpectedUpToThisPoint - totalReceived
          );
        }
      }

      const isPaid = outstandingForThisInstallment === 0;

      return {
        amount: roundCurrency(entry.amount),
        date: entry.date,
        type: type,
        label: entry.label,
        isFinalPayment: isFinalPayment,
        isFirstPayment: isFirstPayment,
        isPaid: isPaid,
        outstandingAmount: roundCurrency(outstandingForThisInstallment),
        cumulativeExpected: cumulativeExpectedUpToThisPoint
      };
    });

    // Find next installment (upcoming, non-final, with outstanding amount)
    const upcomingEntries = enrichedEntries.filter(
      (entry) =>
        entry.outstandingAmount > 0 &&
        entry.date &&
        today &&
        entry.date >= today
    );

    const nextNonFinal =
      upcomingEntries.find((entry) => entry.label !== '') || null;

    // Set next installment with the OUTSTANDING amount, not the original amount
    setNextInstallment(
      nextNonFinal
        ? {
            ...nextNonFinal,
            amount: nextNonFinal.outstandingAmount
          }
        : null
    );
  }, [bookingData]);

  return (
    <Box
      className='payment-successfull-message'
      id={id}
      data-testid='payment-transaction-successful'
      {...rest}
    >
      <Box className='payment-successfull-message__content'>
        <Box className='payment-successfull-message__header'>
          <span
            className={`payment-successfull-message__outer-circle ${
              isACH ? 'payment-successfull-message__outer-circle--ach' : ''
            }`}
          >
            <Check size={32} fill={isACH ? '#155dfc' : '#079455'} />
          </span>
        </Box>

        {/* Main Message */}
        <Typography
          variant='body1'
          className='payment-successfull-message__main-message'
        >
          {isACH
            ? t(
                'trip:paymentSubmittedSuccessfully',
                'Payment submitted successfully'
              )
            : t('trip:paymentSuccessful', 'Payment successful!')}
        </Typography>

        {/* Trip Title */}
        <Typography
          variant='body1'
          className='payment-successfull-message__trip-title'
        >
          {tripTitle}
        </Typography>

        <Divider className='payment-successfull-message__divider' />

        {isLoading ? (
          <PaymentDetailsSkeleton id='payment-details-skeleton' />
        ) : (
          <Box className='payment-successfull-message__payment-details'>
            {/* Payment Amount */}
            <Box className='payment-successfull-message__detail-row'>
              <Typography className='payment-successfull-message__detail-label'>
                {t('bookings:paymentAmount', 'Payment amount')}
              </Typography>
              <Typography className='payment-successfull-message__detail-value'>
                {displayedPayment}
              </Typography>
            </Box>

            {savedPayment?.appliedAmount != null && (
              <Box className='payment-successfull-message__detail-row'>
                <Typography className='payment-successfull-message__detail-label'>
                  {t('bookings:amountAppliedToBooking')}
                </Typography>
                <Typography className='payment-successfull-message__detail-value'>
                  {formatStoredPaymentAmount(
                    savedPayment.appliedAmount,
                    savedPayment.operatorCurrency,
                    locale
                  )}
                </Typography>
              </Box>
            )}

            {/* Balance Due */}
            <Box className='payment-successfull-message__detail-row'>
              <Typography className='payment-successfull-message__detail-label'>
                {t('trip:balanceDue', 'Balance due')}
              </Typography>
              <Typography
                className={`payment-successfull-message__detail-value ${
                  balanceDue === 0
                    ? 'payment-successfull-message__detail-value--paid-in-full'
                    : ''
                }`}
              >
                {balanceDue == null
                  ? t('bookings:paymentDetailsUnavailable')
                  : formatCurrency(balanceDue, operatorConfig)}
              </Typography>
            </Box>

            {/* Next Payment Date (only if balance due exists and next payment date is available) */}
            {balanceDue > 0 && nextPaymentDate && (
              <Box className='payment-successfull-message__detail-row'>
                <Typography className='payment-successfull-message__detail-label'>
                  {nextInstallment?.type !== 'final'
                    ? t('trip:nextPaymentDate', 'Next payment date')
                    : t('trip:finalPaymentDate', 'Final payment date')}
                </Typography>
                <Typography className='payment-successfull-message__detail-value'>
                  {nextPaymentDate}
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Confirmation Email Message */}
        <Typography
          className={`payment-successfull-message__confirmation text-center ${
            isACH ? 'payment-successfull-message__confirmation--with-ach' : ''
          }`}
          sx={{ marginBottom: '4px !important' }}
        >
          {t(
            'trip:confirmationEmailSent',
            'A confirmation email has been sent to:'
            // { email: contactEmail }
          )}
        </Typography>
        <Typography
          className={`payment-successfull-message__confirmation-email text-center `}
        >
          {contactEmail}
        </Typography>

        {/* ACH Processing Message */}
        {isACH && (
          <Typography className='payment-successfull-message__ach-processing text-center '>
            {t(
              'trip:achProcessingMessage',
              'Your payment is processing and typically takes 3–5 business days to complete.'
            )}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

SuccessfulPaymentMessage.propTypes = {
  id: PropTypes.string.isRequired
};

export default SuccessfulPaymentMessage;
